import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Select from '../../components/solid-forms/Select';
import IconRemove from '../../components/icons/IconRemove';
import language from '../../variables/language';

// bare=true skips the own outer box+title - used when this renders as one
// tab's content inside GroupSidebarTabs.jsx instead of its own standalone
// section (the tab bar itself already says what's showing).
export default function GroupMembersPanel({ groupId, refresh, onChange, bare }) {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');

  useEffect(() => {
    axios.get('/api/students?groupe=' + groupId, { withCredentials: true })
      .then(response => setMembers(response.data || []))
      .catch(err => { if (err.response && err.response.status === 401) navigate('/login'); });
    axios.get('/api/students', { withCredentials: true })
      .then(response => setAllStudents(response.data || []))
      .catch(() => {});
  }, [groupId, refresh, navigate]);

  const memberIds = new Set(members.map(student => student.id));
  const availableStudents = allStudents.filter(student => !memberIds.has(student.id));
  const selectOptions = availableStudents.map(student => ({
    id: student.id,
    fullName: (student.name || '') + ' ' + (student.surname || ''),
  }));

  const handleSelectChange = (value) => {
    const parsed = Number(value);
    const isValid = selectOptions.some(option => option.id === parsed);
    setSelectedStudentId(isValid ? parsed : '');
  };

  const addStudent = () => {
    if (!selectedStudentId) return;
    axios.put('/api/students/' + selectedStudentId, JSON.stringify({ groupe: groupId }), {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }).then(() => {
      setSelectedStudentId('');
      onChange();
    }).catch(() => {});
  };

  const removeStudent = (studentId) => {
    if (!window.confirm(language[language.lang].groupCard.removeStudentConfirm)) return;
    axios.put('/api/students/' + studentId + '/removefromgroup', {}, { withCredentials: true })
      .then(() => onChange())
      .catch(() => {});
  };

  const lang = language[language.lang];

  const content = (
    <>
      {members.length === 0 ? (
        <p className="group-lessons-empty">{lang.groupCard.noMembers}</p>
      ) : (
        <ul className="group-members-list">
          {members.map(student => (
            <li className="group-member-row" key={student.id}>
              <span className="group-member-name" onClick={() => navigate('/students/' + student.id)}>
                {student.name} {student.surname}
              </span>
              <span className="group-member-remove" onClick={() => removeStudent(student.id)}>
                <IconRemove />
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="group-add-student-row">
        <Select
          options={selectOptions}
          info={lang.groupCard.addStudentPlaceholder}
          fieldName="fullName"
          value={selectedStudentId}
          inputName="addStudent"
          handleInputChange={handleSelectChange}
          className="submit-field form-selection txt-c select-modal"
        />
        <button type="button" className="group-add-student-btn" disabled={!selectedStudentId} onClick={addStudent}>
          {lang.groupCard.addStudentButton}
        </button>
      </div>
    </>
  );

  if (bare) {
    return content;
  }

  return (
    <div className="group-card-section">
      <h4 className="group-card-section-title">{lang.groupCard.membersTitle}</h4>
      {content}
    </div>
  );
}
